import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaArrowRight,
  FaCheck,
  FaClipboardCheck,
  FaClock,
  FaExchangeAlt,
  FaEye,
  FaLock,
  FaRedoAlt,
  FaSearch,
  FaTimes,
  FaUndoAlt,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isManagementUser, readStoredAdminUser } from './adminAccessControl';
import {
  POS_API_BASE_URL,
  buildApiErrorMessage,
  buildAuthHeaders,
  fetchScopedOutlets,
  formatCurrency,
  formatDateForInput,
  formatShortDateTime,
  getInitialScopedOutlets,
  humanizeToken,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';
import './PosApprovalManager.css';

const REFUND_REASON_OPTIONS = [
  { value: 'customer_return', label: 'Retur pelanggan' },
  { value: 'wrong_item', label: 'Salah item' },
  { value: 'cashier_error', label: 'Salah input kasir' },
  { value: 'duplicate_charge', label: 'Transaksi ganda' },
  { value: 'quality_issue', label: 'Masalah kualitas' },
];

const VOID_REASON_OPTIONS = [
  { value: 'transaction_cancelled', label: 'Transaksi dibatalkan' },
  { value: 'wrong_checkout', label: 'Salah checkout' },
  { value: 'duplicate_transaction', label: 'Transaksi ganda' },
  { value: 'input_error', label: 'Salah input' },
];

const REFUND_SETTLEMENT_OPTIONS = [
  {
    value: 'cash_refund',
    label: 'Refund tunai',
    description: 'Kas atau bank keluar sesuai metode pengembalian.',
  },
  {
    value: 'store_credit',
    label: 'Voucher / saldo retur',
    description: 'Nilai retur disimpan sebagai kredit belanja customer.',
  },
  {
    value: 'exchange_repurchase',
    label: 'Belanja kembali',
    description: 'Retur diterbitkan sebagai saldo yang dipakai ke transaksi pengganti.',
  },
];

const REFUND_SETTLEMENT_METHOD_OPTIONS = {
  cash_refund: [
    { value: 'cash', label: 'Tunai' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'qris', label: 'QRIS' },
  ],
  store_credit: [{ value: 'refund_credit', label: 'Voucher / saldo retur' }],
  exchange_repurchase: [{ value: 'refund_credit', label: 'Saldo retur untuk belanja kembali' }],
};

const APPROVAL_STATUS_OPTIONS = [
  { value: '', label: 'Semua status' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Disetujui' },
  { value: 'rejected', label: 'Ditolak' },
];

const APPROVAL_TYPE_OPTIONS = [
  { value: '', label: 'Semua tipe' },
  { value: 'refund', label: 'Pembatalan' },
  { value: 'void', label: 'Void transaksi' },
];

const getApprovalTypeLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'refund':
      return 'Pembatalan';
    case 'void':
      return 'Void transaksi';
    default:
      return humanizeToken(value);
  }
};

const getStatusVariant = value => {
  switch (String(value || '').toLowerCase()) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    default:
      return 'warning';
  }
};

const getRequestTypeVariant = value => {
  switch (String(value || '').toLowerCase()) {
    case 'refund':
      return 'info';
    case 'void':
      return 'danger';
    default:
      return 'neutral';
  }
};

const getRefundSettlementTypeLabel = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'cash_refund':
      return 'Refund tunai';
    case 'store_credit':
      return 'Voucher / saldo retur';
    case 'exchange_repurchase':
      return 'Belanja kembali';
    default:
      return humanizeToken(value);
  }
};

const getRefundSettlementMethodLabel = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'cash':
      return 'Tunai';
    case 'transfer':
      return 'Transfer';
    case 'qris':
      return 'QRIS';
    case 'refund_credit':
      return 'Saldo retur / voucher';
    default:
      return humanizeToken(value);
  }
};

const getTransactionFinalTotal = transaction =>
  Number(transaction?.total || 0) - Number(transaction?.discount || 0) + Number(transaction?.tax || 0);

const truncateText = (value, maxLength = 48) => {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength - 1)}...`;
};

const aggregateTransactionItems = transaction => {
  const refundableItems = Array.isArray(transaction?.refundable_items)
    ? transaction.refundable_items.filter(item => Number(item?.refundable_quantity || 0) > 0)
    : [];
  if (refundableItems.length > 0) {
    return refundableItems.map(item => ({
      product_id: Number(item?.product_id || 0),
      product_name: item?.product_name || `Product #${item?.product_id || '-'}`,
      quantity: Number(item?.refundable_quantity || 0),
      unit_price: Number(item?.unit_price || 0),
      total: Number(item?.unit_price || 0) * Number(item?.refundable_quantity || 0),
    }));
  }

  const grouped = new Map();
  (Array.isArray(transaction?.items) ? transaction.items : []).forEach(item => {
    const key = String(item?.product_id || '');
    if (!key) {
      return;
    }
    const existing = grouped.get(key) || {
      product_id: Number(item?.product_id || 0),
      product_name: item?.product?.name || `Product #${item?.product_id || '-'}`,
      quantity: 0,
      unit_price: 0,
      total: 0,
    };
    existing.quantity += Number(item?.quantity || 0);
    existing.total += Number(item?.total || 0);
    existing.unit_price = existing.quantity > 0 ? existing.total / existing.quantity : Number(item?.unit_price || 0);
    grouped.set(key, existing);
  });
  return Array.from(grouped.values());
};

function PosApprovalManager({ mode = 'request' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = isManagementUser(currentUser);
  const isApprovalMode = mode === 'approval';
  const requestModulePath = '/pos-pengajuan-pembatalan';
  const approvalModulePath = '/pos-approval-pembatalan';
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const initialOutlets = getInitialScopedOutlets(currentUser);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [dateRange, setDateRange] = useState({
    start_date: formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30))),
    end_date: formatDateForInput(new Date()),
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [refundDraft, setRefundDraft] = useState({
    reason: REFUND_REASON_OPTIONS[0].value,
    settlement_type: REFUND_SETTLEMENT_OPTIONS[0].value,
    settlement_method: REFUND_SETTLEMENT_METHOD_OPTIONS.cash_refund[0].value,
    note: '',
    items: [],
  });
  const [voidDraft, setVoidDraft] = useState({
    reason: VOID_REASON_OPTIONS[0].value,
    note: '',
  });

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => outlet.id === selectedOutlet)?.name || 'Semua outlet',
    [outlets, selectedOutlet]
  );

  const pendingRequestByTransactionId = useMemo(() => {
    const mapping = new Map();
    approvalRequests
      .filter(item => String(item?.status || '').toLowerCase() === 'pending')
      .forEach(item => {
        mapping.set(String(item.transaction_id), item);
      });
    return mapping;
  }, [approvalRequests]);

  const sourceSummary = useMemo(() => (
    transactions.reduce((accumulator, transaction) => {
      const transactionTotal = getTransactionFinalTotal(transaction);
      const isVoided = String(transaction?.document_status || '').toLowerCase() === 'voided';
      const hasPendingRequest = pendingRequestByTransactionId.has(String(transaction?.id || ''));

      accumulator.visibleAmount += transactionTotal;

      if (isVoided) {
        accumulator.voidedCount += 1;
        return accumulator;
      }

      if (hasPendingRequest) {
        accumulator.lockedCount += 1;
        return accumulator;
      }

      accumulator.readyCount += 1;
      accumulator.readyAmount += transactionTotal;
      return accumulator;
    }, {
      readyCount: 0,
      lockedCount: 0,
      voidedCount: 0,
      visibleAmount: 0,
      readyAmount: 0,
    })
  ), [pendingRequestByTransactionId, transactions]);

  const queueSummary = useMemo(() => (
    approvalRequests.reduce((accumulator, request) => {
      const normalizedStatus = String(request?.status || '').toLowerCase();
      const normalizedType = String(request?.request_type || '').toLowerCase();

      if (normalizedStatus === 'pending') {
        accumulator.pendingCount += 1;
      } else if (normalizedStatus === 'approved') {
        accumulator.approvedCount += 1;
      } else if (normalizedStatus === 'rejected') {
        accumulator.rejectedCount += 1;
      }

      if (normalizedType === 'refund') {
        accumulator.refundCount += 1;
      } else if (normalizedType === 'void') {
        accumulator.voidCount += 1;
      }

      return accumulator;
    }, {
      pendingCount: 0,
      approvedCount: 0,
      rejectedCount: 0,
      refundCount: 0,
      voidCount: 0,
    })
  ), [approvalRequests]);

  const sourceHighlights = useMemo(() => ([
    {
      key: 'ready',
      label: 'Siap diajukan',
      value: sourceSummary.readyCount,
      tone: 'success',
    },
    {
      key: 'locked',
      label: 'Terkunci pending',
      value: sourceSummary.lockedCount,
      tone: 'warning',
    },
    {
      key: 'ready-amount',
      label: 'Nilai siap diajukan',
      value: formatCurrency(sourceSummary.readyAmount),
      tone: 'neutral',
    },
  ]), [sourceSummary.lockedCount, sourceSummary.readyAmount, sourceSummary.readyCount]);

  const queueHighlights = useMemo(() => ([
    {
      key: 'pending',
      label: 'Menunggu approval',
      value: queueSummary.pendingCount,
      tone: 'warning',
    },
    {
      key: 'refund',
      label: 'Pengajuan pembatalan',
      value: queueSummary.refundCount,
      tone: 'info',
    },
    {
      key: 'void',
      label: 'Pengajuan void',
      value: queueSummary.voidCount,
      tone: 'danger',
    },
    {
      key: 'reviewed',
      label: 'Sudah direview',
      value: queueSummary.approvedCount + queueSummary.rejectedCount,
      tone: 'success',
    },
  ]), [
    queueSummary.approvedCount,
    queueSummary.pendingCount,
    queueSummary.rejectedCount,
    queueSummary.refundCount,
    queueSummary.voidCount,
  ]);

  const workflowSteps = useMemo(() => ([
    {
      key: 'source',
      icon: FaSearch,
      title: 'Sumber transaksi',
      description: `${sourceSummary.readyCount} transaksi siap diajukan dari scope aktif.`,
    },
    {
      key: 'request',
      icon: FaExchangeAlt,
      title: 'Gerbang pengajuan',
      description: sourceSummary.lockedCount > 0
        ? `${sourceSummary.lockedCount} transaksi terkunci karena masih ada pengajuan pending.`
        : 'Belum ada transaksi yang terkunci oleh pengajuan pending.',
    },
    {
      key: 'queue',
      icon: FaClipboardCheck,
      title: 'Queue approval',
      description: queueSummary.pendingCount > 0
        ? `${queueSummary.pendingCount} pengajuan masih menunggu keputusan reviewer.`
        : 'Queue pending kosong, reviewer bisa fokus ke pengajuan baru.',
    },
  ]), [queueSummary.pendingCount, sourceSummary.lockedCount, sourceSummary.readyCount]);

  const pageHeader = useMemo(() => (
    isApprovalMode
      ? {
          title: 'Approval Pembatalan Transaksi',
          description: 'Review, approve, atau reject pengajuan pembatalan transaksi yang masuk dari outlet dan kasir. Semua keputusan tetap tercatat dengan reason, user, dan timestamp.',
          breadcrumb: 'Approval Pembatalan',
          moduleLink: { to: requestModulePath, label: 'Buka Pengajuan' },
          helperTitle: 'Approval queue pembatalan',
          helperDescription: 'Tim approval fokus pada pengajuan pending, catatan review, dan keputusan akhir tanpa bercampur dengan workspace pengajuan.',
        }
      : {
          title: 'Pengajuan Pembatalan Transaksi',
          description: 'Ajukan pembatalan transaksi atau void dari transaksi POS yang perlu direview oleh staff approval, dengan reason dan catatan yang tercatat rapi.',
          breadcrumb: 'Pengajuan Pembatalan',
          moduleLink: { to: approvalModulePath, label: 'Buka Approval' },
          helperTitle: 'Workspace pengajuan pembatalan',
          helperDescription: 'Pilih transaksi yang valid, lengkapi alasan, lalu kirim ke tim approval tanpa bercampur dengan halaman review keputusan.',
        }
  ), [approvalModulePath, isApprovalMode, requestModulePath]);

  const workspaceHighlights = useMemo(
    () => (isApprovalMode ? queueHighlights : sourceHighlights),
    [isApprovalMode, queueHighlights, sourceHighlights]
  );

  const sourceTableStyles = useMemo(() => ({
    rows: {
      style: {
        minHeight: '76px',
      },
    },
    cells: {
      style: {
        paddingTop: '0.58rem',
        paddingBottom: '0.58rem',
      },
    },
  }), []);

  const queueTableStyles = useMemo(() => ({
    rows: {
      style: {
        minHeight: '82px',
      },
    },
    cells: {
      style: {
        paddingTop: '0.62rem',
        paddingBottom: '0.62rem',
      },
    },
  }), []);

  const initializePage = useCallback(async () => {
    try {
      const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
      if (!token) {
        return;
      }

      setAuthToken(token);
      const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
      setOutlets(scopedOutlets);
      setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
    } catch (error) {
      setErrorMessage(error.message || 'Gagal menyiapkan halaman approval POS.');
    }
  }, [currentUser, navigate, queryOutletId, verifyAndRefreshToken]);

  const fetchTransactions = useCallback(async token => {
    if (!POS_API_BASE_URL || !selectedOutlet) {
      setTransactions([]);
      return;
    }

    setLoadingTransactions(true);
    try {
      const params = new URLSearchParams({
        outlet_id: selectedOutlet,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      });
      if (search.trim()) {
        params.set('search', search.trim());
      }

      const response = await fetch(`${POS_API_BASE_URL}api/transactions/?${params.toString()}`, {
        headers: buildAuthHeaders(token),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat transaksi POS.'));
      }

      const records = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      setTransactions(records);
    } catch (error) {
      setTransactions([]);
      throw error;
    } finally {
      setLoadingTransactions(false);
    }
  }, [dateRange.end_date, dateRange.start_date, search, selectedOutlet]);

  const fetchApprovalRequests = useCallback(async token => {
    if (!POS_API_BASE_URL || !selectedOutlet) {
      setApprovalRequests([]);
      return;
    }

    setLoadingRequests(true);
    try {
      const params = new URLSearchParams({
        outlet_id: selectedOutlet,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      });
      if (search.trim()) {
        params.set('search', search.trim());
      }
      if (statusFilter) {
        params.set('status', statusFilter);
      }
      if (typeFilter) {
        params.set('request_type', typeFilter);
      }

      const response = await fetch(`${POS_API_BASE_URL}api/approval-requests/?${params.toString()}`, {
        headers: buildAuthHeaders(token),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat pengajuan pembatalan POS.'));
      }

      setApprovalRequests(Array.isArray(data?.results) ? data.results : []);
    } catch (error) {
      setApprovalRequests([]);
      throw error;
    } finally {
      setLoadingRequests(false);
    }
  }, [dateRange.end_date, dateRange.start_date, search, selectedOutlet, statusFilter, typeFilter]);

  const loadWorkspace = useCallback(async () => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    setErrorMessage('');
    try {
      await Promise.all([
        fetchTransactions(authToken),
        fetchApprovalRequests(authToken),
      ]);
    } catch (error) {
      setErrorMessage(error.message || 'Gagal memuat workspace approval POS.');
    }
  }, [authToken, fetchApprovalRequests, fetchTransactions, selectedOutlet]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }
    initializePage();
  }, [canAccess, initializePage, navigate]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }
    loadWorkspace();
  }, [authToken, loadWorkspace, selectedOutlet]);

  const handleOpenRefundModal = transaction => {
    setSelectedTransaction(transaction);
    setRefundDraft({
      reason: REFUND_REASON_OPTIONS[0].value,
      settlement_type: REFUND_SETTLEMENT_OPTIONS[0].value,
      settlement_method: REFUND_SETTLEMENT_METHOD_OPTIONS.cash_refund[0].value,
      note: '',
      items: aggregateTransactionItems(transaction).map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        max_quantity: item.quantity,
        quantity: '',
        unit_price: item.unit_price,
        total: item.total,
      })),
    });
    setShowRefundModal(true);
  };

  const handleRefundSettlementTypeChange = value => {
    const normalizedValue = String(value || '').trim().toLowerCase() || REFUND_SETTLEMENT_OPTIONS[0].value;
    const nextMethod = REFUND_SETTLEMENT_METHOD_OPTIONS[normalizedValue]?.[0]?.value || 'cash';
    setRefundDraft(prev => ({
      ...prev,
      settlement_type: normalizedValue,
      settlement_method: nextMethod,
    }));
  };

  const handleOpenVoidModal = transaction => {
    setSelectedTransaction(transaction);
    setVoidDraft({
      reason: VOID_REASON_OPTIONS[0].value,
      note: '',
    });
    setShowVoidModal(true);
  };

  const handleRefundQuantityChange = (productId, value) => {
    setRefundDraft(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.product_id !== productId
          ? item
          : {
              ...item,
              quantity: value,
            }
      ),
    }));
  };

  const submitRefundRequest = async event => {
    event.preventDefault();
    if (!selectedTransaction) {
      return;
    }

    const items = refundDraft.items
      .map(item => ({
        product_id: Number(item.product_id || 0),
        quantity: Number(item.quantity || 0),
      }))
      .filter(item => item.product_id > 0 && item.quantity > 0);

    if (items.length === 0) {
      Swal.fire('Info', 'Pilih minimal satu item retur dengan quantity lebih dari 0.', 'info');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/approval-requests/refund`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          transaction_id: Number(selectedTransaction.id || 0),
          outlet_id: Number(selectedTransaction.outlet_id || 0),
          reason: refundDraft.reason,
          settlement_type: refundDraft.settlement_type,
          settlement_method: refundDraft.settlement_method,
          note: refundDraft.note,
          items,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal membuat pengajuan retur penjualan.'));
      }

      setShowRefundModal(false);
      setSelectedTransaction(null);
      await loadWorkspace();
      Swal.fire('Success', 'Pengajuan retur penjualan berhasil dibuat.', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal membuat pengajuan retur penjualan.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitVoidRequest = async event => {
    event.preventDefault();
    if (!selectedTransaction) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/approval-requests/void`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          transaction_id: Number(selectedTransaction.id || 0),
          outlet_id: Number(selectedTransaction.outlet_id || 0),
          reason: voidDraft.reason,
          note: voidDraft.note,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal membuat pengajuan void transaksi.'));
      }

      setShowVoidModal(false);
      setSelectedTransaction(null);
      await loadWorkspace();
      Swal.fire('Success', 'Pengajuan void transaksi berhasil dibuat.', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal membuat pengajuan void transaksi.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewRequest = async (request, action) => {
    const isReject = action === 'reject';
    const result = await Swal.fire({
      title: isReject ? 'Tolak pengajuan' : 'Setujui pengajuan',
      input: 'textarea',
      inputLabel: isReject ? 'Alasan penolakan' : 'Catatan approval (opsional)',
      inputValue: '',
      inputPlaceholder: isReject ? 'Tulis alasan penolakan agar audit trail jelas' : 'Tambahkan catatan jika perlu',
      inputValidator: value => (isReject && !String(value || '').trim() ? 'Catatan penolakan wajib diisi.' : undefined),
      showCancelButton: true,
      confirmButtonText: isReject ? 'Tolak' : 'Setujui',
      cancelButtonText: 'Batal',
      confirmButtonColor: isReject ? '#dc3545' : '#198754',
    });

    if (!result.isConfirmed) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/approval-requests/${request.id}/${action}`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          note: result.value || '',
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, `Gagal ${action} pengajuan pembatalan.`));
      }

      await loadWorkspace();
      if (showRequestModal) {
        setShowRequestModal(false);
      }
      Swal.fire('Success', isReject ? 'Pengajuan berhasil ditolak.' : 'Pengajuan berhasil disetujui.', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || `Gagal ${action} pengajuan pembatalan.`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenRequestModal = row => {
    setSelectedRequest(row);
    setShowRequestModal(true);
  };

  const transactionColumns = [
    {
      name: 'Transaksi',
      selector: row => row.id,
      sortable: true,
      grow: 1.15,
      minWidth: '200px',
      cell: row => (
        <div className="pos-approval-identity">
          <div className="pos-approval-row-heading">
            <strong>TXN #{row.id || '-'}</strong>
            <span className="pos-approval-pill pos-approval-pill--neutral">
              {Array.isArray(row.items) ? row.items.length : 0} line
            </span>
          </div>
          <small>{formatShortDateTime(row.created_at)} • {row.cashier_name || `Cashier #${row.cashier_id || '-'}`}</small>
        </div>
      ),
    },
    {
      name: 'Pembayaran',
      selector: row => row.payment_method,
      sortable: true,
      grow: 1.1,
      minWidth: '220px',
      cell: row => (
        <div className="pos-approval-identity">
          <div className="pos-approval-row-heading">
            <strong>{humanizeToken(row.payment_method)}</strong>
            <span className={`pos-approval-pill pos-approval-pill--${String(row.document_status || '').toLowerCase() === 'voided' ? 'danger' : 'neutral'}`}>
              {humanizeToken(row.document_status || 'posted')}
            </span>
          </div>
          <small>{truncateText(row.note || 'Tanpa catatan transaksi')}</small>
        </div>
      ),
    },
    {
      name: 'Nilai',
      selector: row => getTransactionFinalTotal(row),
      sortable: true,
      right: true,
      minWidth: '150px',
      maxWidth: '150px',
      cell: row => (
        <div className="pos-approval-identity pos-approval-identity--right">
          <strong>{formatCurrency(getTransactionFinalTotal(row))}</strong>
          <small>{Array.isArray(row.items) ? row.items.length : 0} line item</small>
        </div>
      ),
    },
    {
      name: 'Aksi',
      minWidth: '180px',
      maxWidth: '180px',
      cell: row => {
        const pendingRequest = pendingRequestByTransactionId.get(String(row.id));
        const isVoided = String(row.document_status || '').toLowerCase() === 'voided';
        const actionState = isVoided
          ? { tone: 'danger', label: 'Sudah di-void' }
          : pendingRequest
            ? { tone: 'warning', label: `Terkunci oleh REQ #${pendingRequest.id}` }
            : { tone: 'success', label: 'Siap diajukan' };
        return (
          <div className="pos-approval-action-cell pos-approval-action-cell--source">
            <div className="pos-approval-action-grid">
              <Button
                size="sm"
                variant="outline-primary"
                className="pos-approval-action-button"
                onClick={() => handleOpenRefundModal(row)}
                disabled={isVoided || Boolean(pendingRequest)}
              >
                <FaUndoAlt /> Pembatalan
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                className="pos-approval-action-button"
                onClick={() => handleOpenVoidModal(row)}
                disabled={isVoided || Boolean(pendingRequest)}
              >
                <FaTimes /> Void
              </Button>
            </div>
            <small className={`pos-approval-inline-note pos-approval-inline-note--${actionState.tone}`}>
              {actionState.label}
            </small>
          </div>
        );
      },
    },
  ];

  const approvalColumns = [
    {
      name: 'Pengajuan',
      selector: row => row.id,
      sortable: true,
      grow: 1.05,
      minWidth: '210px',
      cell: row => (
        <div className="pos-approval-identity">
          <div className="pos-approval-row-heading">
            <strong>REQ #{row.id || '-'}</strong>
            <span className={`pos-approval-pill pos-approval-pill--${getRequestTypeVariant(row.request_type)}`}>
              {getApprovalTypeLabel(row.request_type)}
            </span>
          </div>
          <small>TXN #{row.transaction_id || '-'} • {row.requested_by_name || '-'}</small>
        </div>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      minWidth: '135px',
      maxWidth: '135px',
      cell: row => (
        <div className="pos-approval-identity">
          <span className={`pos-approval-pill pos-approval-pill--${getStatusVariant(row.status)}`}>
            {humanizeToken(row.status)}
          </span>
          <small>{row.reviewed_at ? formatShortDateTime(row.reviewed_at) : 'Belum diputuskan'}</small>
        </div>
      ),
    },
    {
      name: 'Reason',
      selector: row => row.reason,
      sortable: true,
      grow: 1.12,
      minWidth: '240px',
      cell: row => (
        <div className="pos-approval-identity">
          <strong>{humanizeToken(row.reason)}</strong>
          <small>
            {String(row.request_type || '').toLowerCase() === 'refund'
              ? `${getRefundSettlementTypeLabel(row.requested_settlement_type)} • ${truncateText(row.request_note || 'Tanpa catatan tambahan', 48)}`
              : truncateText(row.request_note || 'Tanpa catatan tambahan', 64)}
          </small>
        </div>
      ),
    },
    {
      name: 'Nilai',
      selector: row => row.request_total,
      sortable: true,
      right: true,
      minWidth: '150px',
      maxWidth: '150px',
      cell: row => (
        <div className="pos-approval-identity pos-approval-identity--right">
          <strong>{formatCurrency(row.request_total)}</strong>
          <small>{row.item_count || 0} item</small>
        </div>
      ),
    },
    {
      name: 'Pengaju',
      selector: row => row.requested_by_name,
      sortable: true,
      grow: 0.95,
      minWidth: '190px',
      cell: row => (
        <div className="pos-approval-identity">
          <strong>{row.requested_by_name || '-'}</strong>
          <small>{humanizeToken(row.requested_by_actor_type || 'backoffice')} • {formatShortDateTime(row.requested_at)}</small>
        </div>
      ),
    },
    {
      name: 'Aksi',
      minWidth: '170px',
      maxWidth: '170px',
      cell: row => {
        const isPending = String(row.status || '').toLowerCase() === 'pending';
        return (
          <div className="pos-approval-action-cell pos-approval-action-cell--queue">
            <Button size="sm" variant="outline-secondary" className="pos-approval-action-button pos-approval-action-button--detail" onClick={() => handleOpenRequestModal(row)}>
              <FaEye /> Detail
            </Button>
            {isPending ? (
              <div className="pos-approval-action-grid">
                <Button size="sm" variant="outline-success" className="pos-approval-action-button" onClick={() => handleReviewRequest(row, 'approve')}>
                  <FaCheck /> Setujui
                </Button>
                <Button size="sm" variant="outline-danger" className="pos-approval-action-button" onClick={() => handleReviewRequest(row, 'reject')}>
                  <FaTimes /> Tolak
                </Button>
              </div>
            ) : null}
            <small className={`pos-approval-inline-note pos-approval-inline-note--${isPending ? 'warning' : getStatusVariant(row.status)}`}>
              {isPending ? 'Butuh keputusan reviewer' : `Status akhir ${humanizeToken(row.status)}`}
            </small>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaClipboardCheck}
          eyebrow="Catalog & Inventory"
          title={pageHeader.title}
          description={pageHeader.description}
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: pageHeader.breadcrumb },
          ]}
          meta={(
            <div className="d-flex flex-wrap gap-2">
              <span className="soft-stat">
                <FaExchangeAlt /> {selectedOutletName}
              </span>
              <span className="soft-stat">
                <FaClipboardCheck /> {isApprovalMode ? `${approvalRequests.length} pengajuan` : `${transactions.length} transaksi`}
              </span>
            </div>
          )}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              <Button variant="outline-primary" onClick={() => navigate(pageHeader.moduleLink.to)}>
                <FaClipboardCheck /> {pageHeader.moduleLink.label}
              </Button>
              <Button variant="outline-secondary" onClick={loadWorkspace} disabled={loadingTransactions || loadingRequests || submitting}>
                <FaRedoAlt /> Refresh
              </Button>
            </div>
          )}
        />

        <div className="pos-catalog-shell">
          <section className="section-card pos-catalog-form-card">
            <div className="pos-catalog-workspace-head">
              <div>
                <span className="page-eyebrow">{isApprovalMode ? 'Filter approval' : 'Filter pengajuan'}</span>
                <h2>Filter workspace</h2>
                <p>
                  {isApprovalMode
                    ? 'Gunakan outlet, periode, keyword, tipe pengajuan, dan status approval untuk fokus ke queue review yang harus diputuskan.'
                    : 'Gunakan outlet, periode, dan keyword untuk menemukan transaksi POS yang layak diajukan pembatalan oleh tim operasional.'}
                </p>
              </div>
            </div>

            {errorMessage ? (
              <Alert variant="danger" className="mb-0">
                {errorMessage}
              </Alert>
            ) : null}

            <Row className="g-3 align-items-end">
              <Col xl={3} md={6}>
                <Form.Group>
                  <Form.Label>Outlet</Form.Label>
                  <Form.Select value={selectedOutlet} onChange={event => setSelectedOutlet(event.target.value)}>
                    <option value="">Pilih outlet</option>
                    {outlets.map(outlet => (
                      <option key={outlet.id} value={outlet.id}>
                        {outlet.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xl={2} md={6}>
                <Form.Group>
                  <Form.Label>Tanggal awal</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRange.start_date}
                    onChange={event => setDateRange(prev => ({ ...prev, start_date: event.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col xl={2} md={6}>
                <Form.Group>
                  <Form.Label>Tanggal akhir</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRange.end_date}
                    onChange={event => setDateRange(prev => ({ ...prev, end_date: event.target.value }))}
                  />
                </Form.Group>
              </Col>
              {isApprovalMode ? (
                <Col xl={2} md={6}>
                  <Form.Group>
                    <Form.Label>Tipe pengajuan</Form.Label>
                    <Form.Select value={typeFilter} onChange={event => setTypeFilter(event.target.value)}>
                      {APPROVAL_TYPE_OPTIONS.map(option => (
                        <option key={option.value || 'all'} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              ) : null}
              {isApprovalMode ? (
                <Col xl={3} md={6}>
                  <Form.Group>
                    <Form.Label>Status approval</Form.Label>
                    <Form.Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                      {APPROVAL_STATUS_OPTIONS.map(option => (
                        <option key={option.value || 'all'} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              ) : null}
              <Col xl={isApprovalMode ? 8 : 10} md={12}>
                <Form.Group>
                  <Form.Label>Cari cepat</Form.Label>
                  <Form.Control
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder={
                      isApprovalMode
                        ? 'Cari pengajuan id, transaction id, reason, request note, atau pengaju'
                        : 'Cari transaction id, kasir, metode bayar, atau catatan transaksi'
                    }
                  />
                </Form.Group>
              </Col>
              <Col xl={isApprovalMode ? 4 : 2} md={12}>
                <div className="report-center-filter-actions">
                  <Button onClick={loadWorkspace} disabled={loadingTransactions || loadingRequests || submitting}>
                    <FaSearch /> Tampilkan data
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setDateRange({
                        start_date: formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30))),
                        end_date: formatDateForInput(new Date()),
                      });
                      setSearch('');
                      setStatusFilter('');
                      setTypeFilter('');
                    }}
                    disabled={loadingTransactions || loadingRequests || submitting}
                  >
                    <FaRedoAlt /> Reset
                  </Button>
                </div>
              </Col>
            </Row>
          </section>

          <div className="pos-catalog-kpi-grid">
            {workspaceHighlights.map(item => (
              <section key={item.key} className="section-card pos-catalog-kpi-card">
                <span className="pos-catalog-kpi-card__icon">
                  <FaClipboardCheck />
                </span>
                <div className="pos-catalog-kpi-card__copy">
                  <p>{item.label}</p>
                  <strong>{item.value}</strong>
                </div>
              </section>
            ))}
          </div>

          <div className="pos-approval-layout">
            <section className="section-card pos-approval-flow-overview">
              <div className="pos-approval-flow-overview__head">
                <div>
                  <span className="page-eyebrow">Workspace flow</span>
                <h2>Sumber transaksi dan queue approval tersambung</h2>
                  <p>Filter aktif berlaku ke dua panel sekaligus. Alurnya: cari transaksi, ajukan reason yang tepat, lalu review di queue sampai lock transaksi terbuka kembali.</p>
                </div>
                <div className="pos-approval-flow-overview__scope">
                  <span>{selectedOutletName}</span>
                  <small>{dateRange.start_date} s/d {dateRange.end_date}</small>
                </div>
              </div>

              <div className="pos-approval-flow-track">
                {workflowSteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <React.Fragment key={step.key}>
                      <article className="pos-approval-flow-step">
                        <span className="pos-approval-flow-step__icon">
                          <Icon />
                        </span>
                        <div>
                          <strong>{step.title}</strong>
                          <p>{step.description}</p>
                        </div>
                      </article>
                      {index < workflowSteps.length - 1 ? (
                        <div className="pos-approval-flow-track__divider" aria-hidden="true">
                          <FaArrowRight />
                        </div>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>
            </section>

          <section className="section-card pos-approval-tab-shell">
            <div className="pos-approval-tab-shell__header">
              <div>
                <span className="page-eyebrow">{isApprovalMode ? 'Approval module' : 'Pengajuan module'}</span>
                <h2>{pageHeader.helperTitle}</h2>
                <p>{pageHeader.helperDescription}</p>
              </div>
            </div>

            {isApprovalMode ? (
              <section className="section-card pos-catalog-workspace-card pos-approval-panel pos-approval-panel--queue">
                <div className="pos-approval-panel__header">
                  <div className="pos-catalog-workspace-head pos-approval-panel__copy">
                    <div>
                      <span className="page-eyebrow">Queue approval</span>
                      <h2>Queue approval pembatalan</h2>
                  <p>Audit alasan, pengaju, item yang diajukan, dan hasil review. Approval akan menjalankan dokumen pembatalan serta update status transaksi jika pengajuan bertipe void.</p>
                    </div>
                  </div>
                  <div className="pos-approval-panel__badges">
                    <span className="pos-approval-pill pos-approval-pill--warning">{queueSummary.pendingCount} pending</span>
                    <span className="pos-approval-pill pos-approval-pill--info">{approvalRequests.length} total</span>
                  </div>
                </div>

                <div className="pos-approval-panel__stats">
                  {queueHighlights.map(item => (
                    <article key={item.key} className={`pos-approval-mini-stat pos-approval-mini-stat--${item.tone}`}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </article>
                  ))}
                </div>

                <div className="pos-approval-panel__notice pos-approval-panel__notice--queue">
                  <FaClock />
                  <span>Pengajuan pending di modul ini adalah transaksi yang sedang menunggu keputusan approval. Setelah disetujui atau ditolak, histori keputusan dan catatan reviewer akan tetap tersimpan.</span>
                </div>

                <AdminDataTable
                  columns={approvalColumns}
                  data={approvalRequests}
                  progressPending={loadingRequests}
                  pagination
                  highlightOnHover
                  responsive={false}
                  customStyles={queueTableStyles}
                  shellClassName="pos-approval-data-table pos-approval-data-table--queue"
                  tableMinWidth="1095px"
                  emptyTitle="Belum ada pengajuan pembatalan"
                  emptyDescription="Pengajuan pembatalan transaksi yang masuk akan muncul di modul approval ini."
                />
              </section>
            ) : (
              <section className="section-card pos-catalog-workspace-card pos-approval-panel pos-approval-panel--source">
                      <div className="pos-approval-panel__header">
                        <div className="pos-catalog-workspace-head pos-approval-panel__copy">
                          <div>
                            <span className="page-eyebrow">Pengajuan pembatalan</span>
                            <h2>Transaksi POS yang bisa diajukan</h2>
                            <p>Pilih transaksi yang perlu diajukan pembatalan atau void. Jika sebuah transaksi sudah punya pengajuan pending, aksi baru dikunci sampai review sebelumnya selesai.</p>
                          </div>
                        </div>
                        <div className="pos-approval-panel__badges">
                          <span className="pos-approval-pill pos-approval-pill--success">{sourceSummary.readyCount} ready</span>
                          <span className="pos-approval-pill pos-approval-pill--warning">{sourceSummary.lockedCount} locked</span>
                        </div>
                      </div>

                      <div className="pos-approval-panel__stats">
                        {sourceHighlights.map(item => (
                          <article key={item.key} className={`pos-approval-mini-stat pos-approval-mini-stat--${item.tone}`}>
                            <span>{item.label}</span>
                            <strong>{item.value}</strong>
                          </article>
                        ))}
                      </div>

                      <div className="pos-approval-panel__notice">
                        <FaLock />
                        <span>Transaksi dengan pengajuan pending dikunci dari pengajuan baru. Tim approval dapat menindaklanjuti modul approval pembatalan secara terpisah.</span>
                      </div>

                      <AdminDataTable
                        columns={transactionColumns}
                        data={transactions}
                        progressPending={loadingTransactions}
                        pagination
                        highlightOnHover
                        responsive={false}
                        customStyles={sourceTableStyles}
                        shellClassName="pos-approval-data-table pos-approval-data-table--source"
                        tableMinWidth="760px"
                        emptyTitle="Belum ada transaksi POS"
                        emptyDescription="Ubah outlet, periode, atau keyword untuk melihat transaksi yang bisa diajukan pembatalan."
                      />
              </section>
            )}
          </section>
          </div>
        </div>
      </Container>

      <Modal show={showRefundModal} onHide={() => setShowRefundModal(false)} size="lg" centered>
        <Form onSubmit={submitRefundRequest}>
          <Modal.Header closeButton>
            <Modal.Title>Ajukan retur penjualan</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="pos-approval-modal-summary">
              <div>
                <strong>TXN #{selectedTransaction?.id || '-'}</strong>
                <small>{formatShortDateTime(selectedTransaction?.created_at)} • {selectedTransaction?.cashier_name || '-'}</small>
              </div>
              <div>
                <strong>{formatCurrency(getTransactionFinalTotal(selectedTransaction))}</strong>
                <small>{humanizeToken(selectedTransaction?.payment_method)}</small>
              </div>
            </div>

            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Alasan retur</Form.Label>
                  <Form.Select value={refundDraft.reason} onChange={event => setRefundDraft(prev => ({ ...prev, reason: event.target.value }))}>
                    {REFUND_REASON_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Catatan pengajuan</Form.Label>
                  <Form.Control
                    value={refundDraft.note}
                    onChange={event => setRefundDraft(prev => ({ ...prev, note: event.target.value }))}
                    placeholder="Catatan tambahan untuk tim approval"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={12}>
                <Form.Label>Penyelesaian retur</Form.Label>
                <div className="d-flex flex-column gap-2">
                  {REFUND_SETTLEMENT_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleRefundSettlementTypeChange(option.value)}
                      className={`text-start rounded-4 border px-3 py-3 ${
                        refundDraft.settlement_type === option.value
                          ? 'border-primary bg-primary-subtle'
                          : 'border-light bg-white'
                      }`}
                    >
                      <strong className="d-block">{option.label}</strong>
                      <small className="text-muted">{option.description}</small>
                    </button>
                  ))}
                </div>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Metode penyelesaian</Form.Label>
                  <Form.Select
                    value={refundDraft.settlement_method}
                    onChange={event => setRefundDraft(prev => ({ ...prev, settlement_method: event.target.value }))}
                  >
                    {(REFUND_SETTLEMENT_METHOD_OPTIONS[refundDraft.settlement_type] || REFUND_SETTLEMENT_METHOD_OPTIONS.cash_refund).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="pos-approval-item-list">
              {refundDraft.items.map(item => (
                <div key={item.product_id} className="pos-approval-item-row">
                  <div>
                    <strong>{item.product_name}</strong>
                    <small>Sisa retur: {item.max_quantity} • Harga: {formatCurrency(item.unit_price)}</small>
                  </div>
                  <Form.Control
                    type="number"
                    min="0"
                    max={item.max_quantity}
                    value={item.quantity}
                    onChange={event => handleRefundQuantityChange(item.product_id, event.target.value)}
                  />
                </div>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowRefundModal(false)} disabled={submitting}>
              Batal
            </Button>
            <Button type="submit" disabled={submitting}>
              <FaUndoAlt /> Kirim pengajuan
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showVoidModal} onHide={() => setShowVoidModal(false)} centered>
        <Form onSubmit={submitVoidRequest}>
          <Modal.Header closeButton>
            <Modal.Title>Ajukan void transaksi</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="pos-approval-modal-summary">
              <div>
                <strong>TXN #{selectedTransaction?.id || '-'}</strong>
                <small>{formatShortDateTime(selectedTransaction?.created_at)} • {selectedTransaction?.cashier_name || '-'}</small>
              </div>
              <div>
                <strong>{formatCurrency(getTransactionFinalTotal(selectedTransaction))}</strong>
                <small>{Array.isArray(selectedTransaction?.items) ? selectedTransaction.items.length : 0} line item</small>
              </div>
            </div>

            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Alasan void</Form.Label>
                  <Form.Select value={voidDraft.reason} onChange={event => setVoidDraft(prev => ({ ...prev, reason: event.target.value }))}>
                    {VOID_REASON_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Catatan pengajuan</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={voidDraft.note}
                    onChange={event => setVoidDraft(prev => ({ ...prev, note: event.target.value }))}
                    placeholder="Jelaskan kenapa transaksi perlu di-void"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowVoidModal(false)} disabled={submitting}>
              Batal
            </Button>
            <Button type="submit" variant="danger" disabled={submitting}>
              <FaTimes /> Kirim pengajuan
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showRequestModal} onHide={() => setShowRequestModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Detail pengajuan retur / void</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest ? (
            <>
              <div className="pos-approval-modal-summary">
                <div>
                  <strong>REQ #{selectedRequest.id || '-'}</strong>
                  <small>{getApprovalTypeLabel(selectedRequest.request_type)} • TXN #{selectedRequest.transaction_id || '-'}</small>
                </div>
                <div>
                  <strong>{formatCurrency(selectedRequest.request_total)}</strong>
                  <small>{selectedRequest.item_count || 0} item</small>
                </div>
              </div>

              <Row className="g-3 mb-3">
                <Col md={4}>
                  <div className="pos-approval-detail-card">
                    <span>Status</span>
                    <strong>{humanizeToken(selectedRequest.status)}</strong>
                    <small>{selectedRequest.requested_by_name || '-'} • {formatShortDateTime(selectedRequest.requested_at)}</small>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="pos-approval-detail-card">
                    <span>Reason</span>
                    <strong>{humanizeToken(selectedRequest.reason)}</strong>
                    <small>{selectedRequest.request_note || 'Tanpa catatan tambahan'}</small>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="pos-approval-detail-card">
                    <span>Reviewer</span>
                    <strong>{selectedRequest.reviewed_by_name || '-'}</strong>
                    <small>{selectedRequest.reviewed_at ? formatShortDateTime(selectedRequest.reviewed_at) : 'Belum direview'}</small>
                  </div>
                </Col>
              </Row>

              {String(selectedRequest.request_type || '').toLowerCase() === 'refund' ? (
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <div className="pos-approval-detail-card">
                      <span>Penyelesaian</span>
                      <strong>{getRefundSettlementTypeLabel(selectedRequest.requested_settlement_type)}</strong>
                      <small>{getRefundSettlementMethodLabel(selectedRequest.requested_settlement_method)}</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="pos-approval-detail-card">
                      <span>Nota retur</span>
                      <strong>{selectedRequest.refund?.refund_number || '-'}</strong>
                      <small>{selectedRequest.refund?.created_at ? formatShortDateTime(selectedRequest.refund.created_at) : 'Terbit setelah approval'}</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="pos-approval-detail-card">
                      <span>Kode saldo retur</span>
                      <strong>{selectedRequest.refund?.store_credit_code || '-'}</strong>
                      <small>{selectedRequest.refund?.store_credit_code ? 'Gunakan kode ini saat customer belanja lagi.' : 'Tidak diterbitkan untuk refund tunai.'}</small>
                    </div>
                  </Col>
                </Row>
              ) : null}

              <div className="pos-approval-item-list">
                {(Array.isArray(selectedRequest.requested_items) ? selectedRequest.requested_items : []).map(item => (
                  <div key={`${selectedRequest.id}-${item.product_id}`} className="pos-approval-item-row">
                    <div>
                      <strong>{item.product_name || `Product #${item.product_id || '-'}`}</strong>
                      <small>Qty: {item.quantity || 0} • Harga: {formatCurrency(item.unit_price)}</small>
                    </div>
                    <strong>{formatCurrency(item.total)}</strong>
                  </div>
                ))}
              </div>

              {selectedRequest.review_note ? (
                <Alert variant={selectedRequest.status === 'rejected' ? 'danger' : 'success'} className="mt-3 mb-0">
                  <strong>Catatan reviewer:</strong> {selectedRequest.review_note}
                </Alert>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {selectedRequest && String(selectedRequest.status || '').toLowerCase() === 'pending' ? (
            <>
              <Button variant="outline-danger" onClick={() => handleReviewRequest(selectedRequest, 'reject')} disabled={submitting}>
                <FaTimes /> Tolak
              </Button>
              <Button variant="success" onClick={() => handleReviewRequest(selectedRequest, 'approve')} disabled={submitting}>
                <FaCheck /> Setujui
              </Button>
            </>
          ) : null}
          <Button variant="outline-secondary" onClick={() => setShowRequestModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PosApprovalManager;
